/**
 * Phase2Panel.tsx — Calculation results card (right column) for P9_Reveal.
 *
 * Renders kcal/day, recipe count, and supplement count as animated rows.
 * When the backend is still processing, each row shows a shimmer pending
 * state. Once data arrives (or the local fallback kicks in after timeout),
 * the numeric values animate into place via count-up.
 *
 * The personalised "estado bullets" list appears below the rows once the
 * animation reaches the review phase.
 */

import React from 'react';
import { CalculationPreview } from '../../../services/api';
import { FASE_2_ICONS, FASE_2_PENDING_LABELS } from './constants';
import { CalorieGramsRow } from './CalorieGramsRow';
import { ResultRow } from './ResultRow';
import { EstadoBullet } from './buildEstadoBullets';

interface Phase2PanelProps {
  visibleFase2: number;
  effectivePreview: CalculationPreview | null;
  availableRecipesCount: number;
  availableSupplementsCount: number;
  timedOut: boolean;
  preview: CalculationPreview | null;
  /** i18n translator — same signature as AppConfigContext's `t`. */
  t: (key: string, fallback: string, params?: Record<string, string | number>) => string;
  /** Personalised bullets shown after the review phase transition. */
  afinandoBulletsList: React.ReactNode;
}

export const Phase2Panel: React.FC<Phase2PanelProps> = ({
  visibleFase2,
  effectivePreview,
  availableRecipesCount,
  availableSupplementsCount,
  timedOut,
  preview,
  t,
  afinandoBulletsList,
}) => (
  <div
    className="w-full lg:h-full rounded-2xl border p-5 flex flex-col gap-3 shadow-sm"
    style={{ background: 'linear-gradient(135deg, #EAF6FC 0%, #F0FAFB 100%)', borderColor: '#b3e0f0' }}
  >
    <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: '#008BBC' }}>
      {/* Por defecto "Plan nutricional". "Afinando recomendaciones…"
          SOLO cuando se ha disparado el timeout y todavía no
          llegó preview (estado degradado). Si preview llega
          después del timeout, volvemos a "Plan nutricional". */}
      {timedOut && !preview
        ? t('p9.fase2_label_timeout', 'Afinando recomendaciones…')
        : t('p9.fase2_label', 'Plan nutricional')}
    </p>

    {/* Las result rows siempre se renderizan: el componente
        ResultRow maneja su estado pendiente vía la prop `resolved`,
        así que en timeout muestra el pendingLabel con shimmer en
        vez de un valor numérico. Esto evita que el usuario se
        quede mirando un placeholder genérico "Afinando..." sin
        información personalizada. */}
    {/* kcal/día — solo el número, sin gramos (varían por receta). */}
    <CalorieGramsRow
      visible={visibleFase2 > 0}
      resolved={!!effectivePreview?.daily_kcal && visibleFase2 > 0}
      kcal={effectivePreview?.daily_kcal}
    />
    {/* recipes — "4 recetas idóneas". V16 (Santiago 2026-04-28): el
        backend cuenta sin aplicar el allowlist de variant_ids ni el
        check de stock, por lo que devolvía números más altos que
        las recetas que el usuario realmente puede elegir en P10.
        Capamos al `availableRecipes.length` del AppConfigContext
        que SÍ refleja stock+allowlist. */}
    <ResultRow
      stageKey="recipes"
      icon={FASE_2_ICONS.recipes}
      label={FASE_2_PENDING_LABELS.recipes}
      visible={visibleFase2 > 1}
      resolved={effectivePreview?.offerable_recipe_count != null && visibleFase2 > 1}
      numericValue={
        effectivePreview?.offerable_recipe_count != null
          ? Math.min(effectivePreview.offerable_recipe_count, availableRecipesCount)
          : undefined
      }
    />
    {/* supplements — "N suplementos recomendados". El backend devuelve
        supplement_rules.max_recommended (config, p.ej. 3), sin contar los que
        existen de verdad ni filtrar por canal. Capamos a los suplementos
        realmente ofrecibles (config.supplements, ya filtrado por binding de
        plataforma + activos), igual que las recetas se capan a availableRecipes. */}
    <ResultRow
      stageKey="supplements"
      icon={FASE_2_ICONS.supplements}
      label={FASE_2_PENDING_LABELS.supplements}
      visible={visibleFase2 > 2}
      resolved={effectivePreview?.supplement_suggestion_count != null && visibleFase2 > 2}
      numericValue={
        effectivePreview?.supplement_suggestion_count != null
          ? Math.min(effectivePreview.supplement_suggestion_count, availableSupplementsCount)
          : undefined
      }
    />

    {/* Bullets de "Afinando recomendaciones" — siempre visibles,
        independientes del estado de preview. En timeout son la
        única señal de personalización que ve el usuario, así que
        tienen que estar. */}
    {afinandoBulletsList}
  </div>
);

// Re-export EstadoBullet so callers that build the bullets list can import
// from a single p9/ entry point without reaching into buildEstadoBullets.
export type { EstadoBullet };
